import React, { useCallback, useMemo } from "react";
import {
    MultiSelect,
    Loader,
} from "@mantine/core";
import { useActions } from "src/state";
import { UserFragment } from "src/state/gql/_types";
import { GenericFieldProps } from "src/utils/forms";

const toArray = (val: string) =>
    val.length ? val.split(",") : [];

export const AddThreadRecipients: React.FC<GenericFieldProps> = ({ id, name, label, onChange, onBlur, value: valueList, error, required, description, disabled, classNames }) => {
    const [loading, setLoading] = React.useState(false);
    const [options, setOptions] = React.useState<UserFragment[]>([]);
    const { findItems } = useActions().users;

    const value = toArray(valueList || "");

    // --- Debounce helper ---
    const debounce = (fn: Function, delay: number) => {
        let timer: NodeJS.Timeout;
        return (...args: any[]) => {
            clearTimeout(timer);
            timer = setTimeout(() => fn(...args), delay);
        };
    };

    // --- Run backend search ---
    const runSearch = useCallback(
        debounce((q: string) => {
            if (q.length < 1) {
                setOptions([]);
                setLoading(false);
                return;
            }
            setLoading(true);
            findItems({
                where: {
                    _or: [
                        { first_name: { _ilike: `%${q}%` } },
                        { last_name: { _ilike: `%${q}%` } },
                        { email: { _ilike: `%${q}%` } }
                    ]
                }
            })
                .then((users) => setOptions(users))
                .finally(() => setLoading(false));
        }, 300),
        [findItems]
    );

    // Convert options to MultiSelect format
    const selectOptions = useMemo(() => {
        return options.map((user) => ({
            value: user.id,
            label: `${user.first_name} ${user.last_name} (${user.email})`
        }));
    }, [options]);

    // Handle search input
    const handleSearchChange = (query: string) => {
        if (query.trim().length >= 1) {
            runSearch(query.trim());
        } else {
            setOptions([]);
        }
    };

    // Handle value change
    const handleValueChange = (selectedIds: string[]) => {
        onChange(selectedIds.join(","));
    };

    return (
        <MultiSelect
            label={label}
            id={id}
            name={name}
            placeholder="Search users…"
            onBlur={onBlur}
            value={value}
            onChange={handleValueChange}
            error={error}
            required={required}
            disabled={disabled}
            description={description}
            data={selectOptions}
            searchable
            onSearchChange={handleSearchChange}
            rightSection={loading ? <Loader size="xs" /> : null}
            maxDropdownHeight={200}
            nothingFoundMessage="No users found"
            clearable
            classNames={classNames}
        />
    );
};